import merge from "lodash/merge";
import { Action, Reducer } from "redux";

const mergeReducer = <S, A extends Action, E extends Action, P = S>(
    baseReducer: Reducer<S, A, P>,
    extendedReducer: Reducer<S, E, P>,
) => {
    return ((state: S, action: A | E): S => {
        const baseState = baseReducer(state, action as A);
        const extensionState = extendedReducer(state, action as E);

        if (!state) {
            // The initial state is not defined yet, we merge both results to enable all initial values.
            return merge({}, baseState, extensionState);
        }

        const baseStateHasChanged = baseState !== state;
        const extensionStateHasChanged = extensionState !== state;

        if (baseStateHasChanged && extensionStateHasChanged) {
            // If both states have changed, the native action was overriden by the extension, we return the extension result only.
            return extensionState;
        }

        if (baseStateHasChanged) {
            // If only the base state has changed, we look for an extend action in the extended reducer.
            // The reducer directly returns the extended state (if existing) or the unmodified base state (if not existing).
            return extendedReducer(baseState, {
                ...(action as E),
                type: `After-${action.type}`,
            });
        }

        if (extensionStateHasChanged) {
            // If only the extension state changed, the action is not native, we can safely return the extension result.
            return extensionState;
        }

        // Nothing has changed, return the original state.
        return state;
    }) as Reducer<S, A, P>;
};

export default mergeReducer;
